'use client';

import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string | string[];
}

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: "1. Why does FINVION Profit Expander exist?",
      answer: "Most Amazon tools optimize for ACOS and ROAS. FINVION was created because sellers needed something different: true, keyword-level profit clarity before touching any bid. Profit is the one metric every seller cares about, yet no mainstream tool reconstructs real profit per keyword by combining Ads API data, SP-API orders, FBA fees, refunds, and your COGS. FINVION was built to close this gap and give sellers the financial truth their ad tools don't calculate."
    },
    {
      question: "2. What makes FINVION different from existing PPC tools?",
      answer: [
        "Other tools focus on automation or bulk rules. FINVION focuses on profit visibility + safe manual decisions.",
        "Key differences:",
        "• reconstructs actual keyword-level profit, including refunds and cost creep",
        "• calculates CPC safety ceilings based on your true margins",
        "• provides conservative manual bid recommendations",
        "• uses strict safety rails instead of automation",
        "• no campaign restructuring, no AI \"guessing,\" no risk to rankings",
        "",
        "FINVION supports your decisions without taking over your account."
      ]
    },
    {
      question: "3. What is the key difference between manual profit calculation and FINVION's Profit Expander method?",
      answer: "Manual profit tracking is slow, error-prone, and usually incomplete. Most spreadsheets only combine a few variables, such as spend, sales, and ACOS. They rarely include refund drag, cost creep, true COGS, or keyword-level attribution. This leads to decisions based on partial data. FINVION uses a unified profit reconstruction model that merges Ads API performance, SP-API orders, FBA fees, referral fees, refunds, and your exact COGS. The result is a precise, daily profit calculation for every keyword. Where spreadsheets show approximations, FINVION shows the full financial truth behind each bid decision."
    },
    {
      question: "4. Why does FINVION deliver better results than spreadsheets, manual comparison, or general PPC tools?",
      answer: [
        "FINVION eliminates guesswork. Instead of switching between dashboards, calculating margins manually, or relying on ACOS-only tools, FINVION shows:",
        "• true profit per keyword",
        "• CPC safety ceilings based on actual margins",
        "• refund drag and cost creep",
        "• stable, conservative bid suggestions",
        "",
        "Most PPC tools optimize for ACOS, ROAS, or click efficiency. FINVION optimizes for real financial outcomes. Most spreadsheets depend on manual updates. FINVION updates daily, automatically.",
        "",
        "This combination of accuracy, completeness, and safe bid logic results in more consistent margin protection and faster waste reduction compared to manual workflows or generic optimization platforms."
      ]
    },
    {
      question: "5. Can I use FINVION alongside Teikametrics, Perpetua, Eva.guru, etc.?",
      answer: [
        "Yes. FINVION is specifically designed to run parallel to any automated tool.",
        "",
        "It does not:",
        "• change bids automatically",
        "• overwrite rules",
        "• restructure ad groups",
        "• inject external campaigns",
        "",
        "It simply gives you real profit signals and safe bid suggestions that you apply manually. This makes FINVION the perfect \"profit truth layer\" on top of whatever automation you already use."
      ]
    },
    {
      question: "6. Can FINVION calculate how much time I save compared to manual analysis?",
      answer: [
        "Yes. FINVION replaces dozens of manual steps that typically take advertisers hours every week. Manual workflows require downloading reports, merging spreadsheets, calculating margins, tracking CPC drift, checking refunds, comparing trends, and evaluating every keyword.",
        "",
        "FINVION automates the entire evaluation layer by:",
        "• reconstructing true profit per keyword daily",
        "• combining Ads API + SP-API + fees + refunds + COGS",
        "• detecting CPC drift and margin erosion",
        "• identifying profit leaks automatically",
        "• generating safe bid recommendations instantly",
        "",
        "Instead of spending hours collecting and calculating data, advertisers only review the output and apply recommendations. Most users reduce weekly PPC analysis time by 60 to 85 percent depending on account size."
      ]
    },
    {
      question: "7. Will FINVION still deliver results if I don't use automation at all?",
      answer: "Yes. FINVION works best with manual control, because you apply the adjustments yourself. The tool's recommendations are intentionally conservative, profit-first, and easy to implement. Most users see reduced wasted spend and healthier margin within the first 7–21 days, even with manual-only workflows."
    },
    {
      question: "8. Will using FINVION harm my Amazon ranking?",
      answer: [
        "No. FINVION's recommendation engine is designed around ranking safety. Every suggestion is bounded by:",
        "• max +12 percent increase",
        "• max –20 percent decrease",
        "• HOLD if data is unstable",
        "• no recommendations on volatile keywords",
        "",
        "There is no aggressive bidding or algorithmic experimentation. FINVION protects ranking instead of risking it."
      ]
    },
    {
      question: "9. Does FINVION require long setup or historical data?",
      answer: "No. Once your Ads API and SP-API permissions are connected, FINVION delivers meaningful insights within 24–48 hours. There is no warm-up period, no training phase, and no algorithm ramp-up."
    },
    {
      question: "10. What type of sellers or agencies get the most value from FINVION?",
      answer: [
        "FINVION is ideal for:",
        "• sellers spending €3,000–€100,000+ per month",
        "• agencies managing multiple accounts",
        "• brands that prefer manual control",
        "• advertisers who want profit clarity instead of ACOS guessing",
        "",
        "If profit protection is the priority, FINVION delivers immediate value."
      ]
    },
    {
      question: "11. What data does FINVION use to calculate real profit?",
      answer: [
        "FINVION merges data from:",
        "• Amazon Ads API",
        "• Amazon SP-API orders",
        "• FBA fees",
        "• referral fees",
        "• refunds and return costs",
        "• your COGS inputs",
        "",
        "This produces a true daily profit model for every keyword, ASIN, and campaign."
      ]
    },
    {
      question: "12. What does \"manual bid recommendation\" actually mean?",
      answer: [
        "FINVION gives you safe, conservative adjustments based on profit logic. You apply them inside Amazon Ads with one click. This ensures:",
        "• full user control",
        "• no automation risk",
        "• no sudden bid shocks",
        "• ranking stability",
        "",
        "FINVION acts like a financial co-pilot, not a replacement of your processes."
      ]
    },
    {
      question: "13. How does FINVION calculate bid recommendations in a way a human cannot reliably do?",
      answer: [
        "FINVION combines data points that a human cannot track or update consistently at keyword-level scale. Every recommendation is based on a proprietary profit reconstruction model that merges:",
        "",
        "• Amazon Ads API performance",
        "• SP-API orders and sales attribution",
        "• FBA fees",
        "• referral fees",
        "• refund impact",
        "• cost creep",
        "• your exact COGS",
        "• keyword intent classification",
        "• semantic keyword relevance",
        "• CPC drift patterns",
        "• margin thresholds",
        "",
        "FINVION recalculates all of these elements daily and converts them into a CPC safety ceiling, a profit range, and a safe adjustment window. This allows the system to estimate the maximum bid that still protects margin, the minimum bid that maintains traffic flow, and whether a keyword should be held, raised, or reduced.",
        "",
        "A human can attempt this manually, but it is extremely difficult because:",
        "• the number of variables changes daily",
        "• refund and fee impact lag behind ad data",
        "• CPC drift is invisible without modeling",
        "• relevance shifts aren't obvious without semantic checks",
        "• margin erosion often happens slowly and is easy to miss",
        "• spreadsheets cannot update at keyword-level scale reliably",
        "",
        "FINVION evaluates these signals simultaneously, identifies hidden profit leaks, and translates them into conservative, ranking-safe manual bid suggestions. The result is a level of accuracy and consistency that is nearly impossible to achieve with spreadsheets or manual reviews."
      ]
    },
    {
      question: "14. When will automated bidding or rank tracking be added?",
      answer: [
        "Those features arrive in later versions:",
        "• automated bidding → not part of MVP 0.5",
        "• visibility and ranking tracking → MVP 0.9",
        "• price optimization & LaunchDrive → later milestones"
      ]
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const renderAnswer = (answer: string | string[]) => {
    if (typeof answer === 'string') {
      return <p data-component-id="src/app/landing-page/components/FAQSection.tsx:184:13" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="184" data-component-file="FAQSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B16px%5D%20md%3Atext-%5B18px%5D%20text-%5B%232f3640%5D%20leading-%5B140%25%5D%22%7D" className="text-[16px] md:text-[18px] text-[#2f3640] leading-[140%]">{answer}</p>;
    }
    
    return (
      <div data-component-id="src/app/landing-page/components/FAQSection.tsx:188:6" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="188" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-%5B16px%5D%20md%3Atext-%5B18px%5D%20text-%5B%232f3640%5D%20leading-%5B140%25%5D%20space-y-3%22%7D" className="text-[16px] md:text-[18px] text-[#2f3640] leading-[140%] space-y-3">
        {answer.map((line, idx) => (
          <p data-component-id="src/app/landing-page/components/FAQSection.tsx:190:10" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="190" data-component-file="FAQSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D" key={idx}>{line}</p>
        ))}
      </div>
    );
  };

  return (
    <section data-component-id="src/app/landing-page/components/FAQSection.tsx:197:4" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="197" data-component-file="FAQSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22faq%22%2C%22className%22%3A%22py-24%20md%3Apy-32%20bg-white%20px-4%20md%3Apx-16%22%7D" id="faq" className="py-24 md:py-32 bg-white px-4 md:px-16">
      <div data-component-id="src/app/landing-page/components/FAQSection.tsx:198:6" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="198" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-%5B1200px%5D%20mx-auto%22%7D" className="max-w-[1200px] mx-auto">
        <h2 data-component-id="src/app/landing-page/components/FAQSection.tsx:199:8" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="199" data-component-file="FAQSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-%5B32px%5D%20md%3Atext-%5B40px%5D%20font-bold%20text-%5B%234cd137%5D%20mb-12%20text-center%22%2C%22textContent%22%3A%22Q%26A%22%7D" className="text-[32px] md:text-[40px] font-bold text-[#4cd137] mb-12 text-center">
          Q&A
        </h2>

        <div data-component-id="src/app/landing-page/components/FAQSection.tsx:203:8" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="203" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%20max-w-4xl%20mx-auto%22%7D" className="space-y-4 max-w-4xl mx-auto">
          {faqs.map((faq, index) => (
            <div data-component-id="src/app/landing-page/components/FAQSection.tsx:205:12" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="205" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-%5B%23f5f6fa%5D%20rounded-lg%20overflow-hidden%20border-2%20border-%5B%23dcdde1%5D%20hover%3Aborder-%5B%234cd137%5D%20transition-colors%22%7D"
              key={index}
              className="bg-[#f5f6fa] rounded-lg overflow-hidden border-2 border-[#dcdde1] hover:border-[#4cd137] transition-colors"
            >
              <button data-component-id="src/app/landing-page/components/FAQSection.tsx:209:14" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="209" data-component-file="FAQSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20text-left%20px-6%20py-5%20flex%20items-center%20justify-between%22%7D"
                onClick={() => toggleFAQ(index)}
                className="w-full text-left px-6 py-5 flex items-center justify-between"
              >
                <span data-component-id="src/app/landing-page/components/FAQSection.tsx:213:16" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="213" data-component-file="FAQSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B16px%5D%20md%3Atext-%5B18px%5D%20font-semibold%20text-%5B%232f3640%5D%20pr-4%22%7D" className="text-[16px] md:text-[18px] font-semibold text-[#2f3640] pr-4">
                  {faq.question}
                </span>
                <span data-component-id="src/app/landing-page/components/FAQSection.tsx:216:16" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="216" data-component-file="FAQSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-%5B24px%5D%20text-%5B%234cd137%5D%20flex-shrink-0%22%7D" className="text-[24px] text-[#4cd137] flex-shrink-0">
                  {openIndex === index ? '−' : '+'}
                </span>
              </button>
              
              {openIndex === index && (
                <div data-component-id="src/app/landing-page/components/FAQSection.tsx:222:16" data-component-path="src/app/landing-page/components/FAQSection.tsx" data-component-line="222" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20pb-5%22%7D" className="px-6 pb-5">
                  {renderAnswer(faq.answer)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;